import React, {
	memo,
	useCallback,
	useEffect,
	useMemo
} from 'react';
import PropTypes from 'prop-types';
import {
	useFlexLayout,
	useFilters,
	usePagination,
	useResizeColumns,
	useRowSelect,
	useSortBy,
	useTable
} from 'react-table';

import { log } from '../../services/utils';

import IndeterminateCheckbox from './IndeterminateCheckbox';
import TableHeader from './TableHeader';
import TableFilters from './TableFilters';
import TableBody from './TableBody';
import TableFooter from './TableFooter';
import RecordCount from './RecordCount';
import DefaultColumnFilter from './ColumnFilters/DefaultColumnFilter';

import styles from './table.module.css';

const Table = (	{
	columns,
	currentRowRight,
	data,
	defaultPageSize = 10,
	hasPermission = true,
	highlightCurrentRow,
	isLoading,
	onRowClick,
	rowStyles = [],
	showCount = true,
	showEmptyRows = true,
	showFilters = true,
	showPagination = true,
	selectable = false,
	setTableState,
	style,
	tableState = {}
} ) => {
	const defaultColumn = useMemo( () => ( {
		Filter: DefaultColumnFilter,
		setTableState
	} ), [ setTableState ] );
	const useControlledState = useCallback( state => ( {
		...state,
		...tableState
	} ), [ tableState ] );

	const selectionHeaderCell = useCallback( ( { getToggleAllRowsSelectedProps } ) => (
		<div style={ { margin: '2.5px 8px 0' } }>
			<IndeterminateCheckbox { ...getToggleAllRowsSelectedProps() } />
		</div>
	), [] );

	const selectionCell = useCallback( ( { row } ) => (
		<div style={ { margin: '2.5px 8px 0' } }>
			<IndeterminateCheckbox { ...row.getToggleRowSelectedProps() } />
		</div>
	), [] );

	const {
		getTableProps,
		getTableBodyProps,
		headerGroups,
		prepareRow,
		page, // has only the rows for the active page
		rows,
		canPreviousPage,
		canNextPage,
		pageCount,
		gotoPage,
		nextPage,
		previousPage,
		selectedFlatRows,
		setPageSize,
		state: {
			pageIndex,
			pageSize
		}
	} = useTable(
		{
			autoResetPage: false,
			columns,
			data,
			defaultColumn,
			pageSize: defaultPageSize,
			useControlledState
		},
		useFlexLayout,
		useFilters,
		useResizeColumns,
		useSortBy,
		usePagination,
		useRowSelect,
		hooks => {
			if ( selectable ) {
				hooks.visibleColumns.push( columns => [
					{
						id: 'selection',
						Header: selectionHeaderCell,
						Cell: selectionCell,
						disableFilters: true,
						disableResizing: true,
						width: 43
					},
					...columns
				] );
			}
		}
	);

	useEffect( () => {
		if ( setTableState ) {
			setTableState( prev => ( {
				...prev,
				rows
			} ) );
		}
	}, [
		rows,
		setTableState
	] );

	useEffect( () => {
		if ( setTableState ) {
			const selectedIds = selectedFlatRows.map( d => d.original.id );

			setTableState( prev => ( {
				...prev,
				selectedIds
			} ) );
		}
	}, [
		selectedFlatRows,
		setTableState
	] );

	useEffect( () => {
		if ( setTableState && rows.length / pageSize <= pageIndex ) {
			setTableState( prev => ( {
				...prev,
				pageIndex: rows.length > 0
					? Math.ceil( rows.length / pageSize ) - 1
					: 0
			} ) );
		}
	}, [
		rows.length,
		setTableState,
		pageIndex,
		pageSize
	] );

	const pageIndexProp = useMemo( () => parseInt( pageIndex ), [ pageIndex ] );

	const recordCount = useMemo(
		() => showCount
			? <RecordCount
				pageLength={ page.length }
				pageIndex={ pageIndexProp }
				pageSize={ pageSize }
				rows={ rows }
			/>
			: null,
		[
			showCount,
			page.length,
			pageIndexProp,
			pageSize,
			rows
		]
	);

	const handleGotoPage = useCallback(
		index => setTableState
			? index >= 0 && index < pageCount
				? setTableState( prev => ( {
					...prev,
					pageIndex: index
				} ) )
				: null
			: gotoPage( index ),
		[
			gotoPage,
			pageCount,
			setTableState
		]
	);

	const handleNextPage = useCallback(
		() => setTableState
			? pageIndex < pageCount
				? setTableState( prev => ( {
					...prev,
					pageIndex: prev.pageIndex + 1
				} ) )
				: null
			: nextPage(),
		[
			nextPage,
			pageCount,
			setTableState,
			pageIndex
		]
	);

	const handlePreviousPage = useCallback(
		() => setTableState
			? pageIndex > 0
				? setTableState( prev => ( {
					...prev,
					pageIndex: prev.pageIndex - 1
				} ) )
				: null
			: previousPage(),
		[
			previousPage,
			setTableState,
			pageIndex
		]
	);

	const handleSetPageSize = useCallback(
		pageSize => setTableState
			? setTableState( prev => ( {
				...prev,
				pageIndex: rows.length / pageSize <= prev.pageIndex
					? Math.ceil( rows.length / pageSize ) - 1
					: prev.pageIndex,
				pageSize
			} ) )
			: setPageSize( pageSize ),
		[
			rows.length,
			setPageSize,
			setTableState
		]
	);

	if ( !hasPermission ) {
		return <p>Access denied.</p>;
	}

	return (
		<>
			{ log( 'table' ) }
			<div
				className={ styles.table }
				{ ...getTableProps() }
				style={ style }
			>
				<TableHeader
					headerGroups={ headerGroups }
					getTableBodyProps={ getTableBodyProps }
					setTableState={ setTableState }
				/>
				{ showFilters
					? <TableFilters
						headerGroups={ headerGroups }
						getTableBodyProps={ getTableBodyProps }
					/>
					: null
				}
				<TableBody
					currentRowRight={ currentRowRight }
					getTableBodyProps={ getTableBodyProps }
					highlightCurrentRow={ highlightCurrentRow }
					isLoading={ isLoading }
					onClick={ onRowClick }
					page={ page }
					pageSize={ pageSize }
					prepareRow={ prepareRow }
					recordCount={ recordCount }
					rowStyles={ rowStyles }
					showEmptyRows={ showEmptyRows }
				/>
				{ showPagination
					? <TableFooter
						canNextPage={ canNextPage }
						canPreviousPage={ canPreviousPage }
						gotoPage={ handleGotoPage }
						isLoading={ isLoading }
						nextPage={ handleNextPage }
						noRecords={ !data.length }
						pageCount={ pageCount }
						pageIndex={ pageIndex }
						pageSize={ pageSize }
						previousPage={ handlePreviousPage }
						setPageSize={ handleSetPageSize }
					/>
					: null
				}
			</div>
		</>
	);
};

Table.propTypes = {
	columns: PropTypes.array.isRequired,
	currentRowRight: PropTypes.func,
	data: PropTypes.array.isRequired,
	defaultPageSize: PropTypes.number,
	hasPermission: PropTypes.bool,
	highlightCurrentRow: PropTypes.bool,
	isLoading: PropTypes.bool,
	onRowClick: PropTypes.func,
	rowStyles: PropTypes.arrayOf( PropTypes.shape( {
		style: PropTypes.object,
		condition: PropTypes.func
	} ) ),
	showCount: PropTypes.bool,
	showEmptyRows: PropTypes.bool,
	showFilters: PropTypes.bool,
	showPagination: PropTypes.bool,
	selectable: PropTypes.bool,
	setTableState: PropTypes.func,
	style: PropTypes.object,
	tableState: PropTypes.object
};

export default memo( Table );
